import jsPDF from "jspdf";
import type { CapturedBlock } from "./captureBlocks";
import type { PdfPageLayout, PlacedBlock } from "./planPdfPages";
import type { DataTramLogo } from "./loadDataTramLogo";

export interface PdfHeaderInfo {
  dashboardName: string;
  dateRangeLabel: string;
  generatedOnLabel: string;
  logo: DataTramLogo | null;
}

function sliceCanvas(
  source: HTMLCanvasElement,
  sliceIndex: number,
  sliceCount: number,
  headerRowTopPx: number,
  headerRowHeightPx: number,
  startRatio: number,
  heightRatio: number,
  omitLeadingHeader: boolean,
): { canvas: HTMLCanvasElement; heightScale: number } {
  const headerPx = headerRowTopPx + headerRowHeightPx;
  const skipHeader = omitLeadingHeader && headerPx > 0;

  if (sliceCount <= 1) {
    if (!skipHeader || headerPx >= source.height) {
      return { canvas: source, heightScale: 1 };
    }
    const bodyHeightPx = source.height - headerPx;
    const cropped = document.createElement("canvas");
    cropped.width = source.width;
    cropped.height = bodyHeightPx;
    const cropCtx = cropped.getContext("2d");
    if (cropCtx) {
      cropCtx.drawImage(
        source,
        0, headerPx, source.width, bodyHeightPx,
        0, 0, source.width, bodyHeightPx,
      );
    }
    return { canvas: cropped, heightScale: bodyHeightPx / source.height };
  }
  let startYPx = Math.round(source.height * startRatio);
  let heightPx = Math.max(1, Math.min(
    Math.round(source.height * heightRatio),
    source.height - startYPx,
  ));
  let heightScale = 1;
  if (skipHeader && sliceIndex === 0 && headerPx < heightPx) {
    startYPx += headerPx;
    heightPx -= headerPx;
    const allocatedPx = Math.max(1, Math.round(source.height * heightRatio));
    heightScale = heightPx / allocatedPx;
  }

  const repeatHeader = sliceIndex > 0 && headerRowHeightPx > 0 && headerRowHeightPx < heightPx;
  const sliceCanvasEl = document.createElement("canvas");
  sliceCanvasEl.width = source.width;
  sliceCanvasEl.height = heightPx;
  const ctx = sliceCanvasEl.getContext("2d");
  if (ctx) {
    if (repeatHeader) {
      ctx.drawImage(
        source,
        0, headerRowTopPx, source.width, headerRowHeightPx,
        0, 0, source.width, headerRowHeightPx,
      );
      const bodyHeightPx = heightPx - headerRowHeightPx;
      ctx.drawImage(
        source,
        0, startYPx, source.width, bodyHeightPx,
        0, headerRowHeightPx, source.width, bodyHeightPx,
      );
    } else {
      ctx.drawImage(
        source,
        0, startYPx, source.width, heightPx,
        0, 0, source.width, heightPx,
      );
    }
  }
  return { canvas: sliceCanvasEl, heightScale };
}

const LOGO_HEADER_HEIGHT_MM = 7;
const LOGO_FOOTER_HEIGHT_MM = 4;

function drawLogoOrText(
  pdf: jsPDF,
  logo: DataTramLogo | null,
  xMm: number,
  baselineYMm: number,
  heightMm: number,
): number {
  if (!logo) {
    pdf.text("DataTram", xMm, baselineYMm);
    return pdf.getTextWidth("DataTram");
  }
  const widthMm = (logo.widthPx / logo.heightPx) * heightMm;
  pdf.addImage(logo.dataUrl, "PNG", xMm, baselineYMm - heightMm * 0.8, widthMm, heightMm);
  return widthMm;
}

function drawHeader(pdf: jsPDF, layout: PdfPageLayout, header: PdfHeaderInfo): void {
  const { marginMm, pageWidthMm, headerHeightMm } = layout;
  pdf.setFontSize(14);
  pdf.setTextColor(20, 20, 20);
  drawLogoOrText(pdf, header.logo, marginMm, marginMm + 5, LOGO_HEADER_HEIGHT_MM);

  pdf.setFontSize(11);
  pdf.setTextColor(60, 60, 60);
  pdf.text(header.dashboardName, marginMm, marginMm + 11);

  pdf.setFontSize(9);
  pdf.setTextColor(90, 90, 90);
  const rightText = `${header.dateRangeLabel}  |  ${header.generatedOnLabel}`;
  pdf.text(rightText, pageWidthMm - marginMm, marginMm + 5, { align: "right" });

  const dividerYMm = marginMm + headerHeightMm - 4;
  pdf.setDrawColor(220, 220, 220);
  pdf.setLineWidth(0.2);
  pdf.line(marginMm, dividerYMm, pageWidthMm - marginMm, dividerYMm);
}

function drawFooter(
  pdf: jsPDF,
  layout: PdfPageLayout,
  pageNumber: number,
  pageCount: number,
  logo: DataTramLogo | null,
): void {
  const { marginMm, pageWidthMm, pageHeightMm } = layout;
  const footerYMm = pageHeightMm - marginMm / 2;

  pdf.setFontSize(8);
  pdf.setTextColor(140, 140, 140);
  drawLogoOrText(pdf, logo, marginMm, footerYMm, LOGO_FOOTER_HEIGHT_MM);
  pdf.text(`Page ${pageNumber} of ${pageCount}`, pageWidthMm - marginMm, footerYMm, { align: "right" });
}

export function renderPdf(
  layout: PdfPageLayout,
  capturedBlocks: CapturedBlock[],
  header: PdfHeaderInfo,
): jsPDF {
  const pdf = new jsPDF("l", "mm", "a4");
  const blocksById = new Map(capturedBlocks.map(block => [block.id, block]));
  const pageCount = layout.pages.length;

  layout.pages.forEach((pageBlocks: PlacedBlock[], pageIndex: number) => {
    if (pageIndex > 0) {
      pdf.addPage("a4", "l");
    }
    drawHeader(pdf, layout, header);

    for (const placed of pageBlocks) {
      const captured = blocksById.get(placed.id);
      if (!captured) {
        continue;
      }
      const siblingSlices = layout.pages.flat().filter(block => block.id === placed.id);
      const totalSliceHeightMm = siblingSlices.reduce((sum, block) => sum + block.heightMm, 0) || captured.heightMm;
      const startYMm = siblingSlices
        .filter(block => block.sliceIndex < placed.sliceIndex)
        .reduce((sum, block) => sum + block.heightMm, 0);
      const contentTopMm = layout.marginMm + layout.headerHeightMm;
      const omitLeadingHeader = placed.sliceIndex === 0
        && placed.yMm > contentTopMm + 0.5
        && captured.headerRowHeightPx > 0;
      const canvasSlice = sliceCanvas(
        captured.canvas,
        placed.sliceIndex,
        placed.sliceCount,
        captured.headerRowTopPx,
        captured.headerRowHeightPx,
        startYMm / totalSliceHeightMm,
        placed.heightMm / totalSliceHeightMm,
        omitLeadingHeader,
      );
      // JPEG at high quality instead of PNG: a lossless PNG of a full table screenshot (product
      // thumbnails, colored status pills, etc.) can be many times larger as a base64 string, and
      // jsPDF concatenating several such strings into one PDF can exceed the JS engine's max
      // string length ("RangeError: Invalid string length") before the file ever gets written.
      // 0.92 keeps text and UI crisp while cutting payload size drastically.
      const imageData = canvasSlice.canvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(
        imageData,
        "JPEG",
        placed.xMm,
        placed.yMm,
        placed.widthMm,
        placed.heightMm * canvasSlice.heightScale,
      );
    }

    drawFooter(pdf, layout, pageIndex + 1, pageCount, header.logo);
  });

  return pdf;
}
